import { useState } from "react";

interface ProcedureNarrativeProps {
  steps: string[];
  onStepsChange: (steps: string[]) => void;
  readOnly?: boolean;
}

const MAX_STEP_CHARS = 1000;

export default function ProcedureNarrative({
  steps,
  onStepsChange,
  readOnly = false,
}: ProcedureNarrativeProps) {
  const [newStep, setNewStep] = useState("");

  const handleAdd = () => {
    if (!newStep.trim()) return;
    onStepsChange([...steps, newStep.trim()]);
    setNewStep("");
  };

  const handleRemove = (index: number) => {
    onStepsChange(steps.filter((_, i) => i !== index));
  };

  const handleEdit = (index: number, value: string) => {
    onStepsChange(steps.map((s, i) => (i === index ? value : s)));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...steps];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onStepsChange(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === steps.length - 1) return;
    const updated = [...steps];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onStepsChange(updated);
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-white/[0.05]">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
            Procedure Narrative
          </h3>
          <div className="flex items-center gap-2">
            {readOnly && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                Read Only
              </span>
            )}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {steps.length} step{steps.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-3">
        {steps.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            <p className="text-sm">No procedure steps yet.</p>
            {!readOnly && (
              <p className="text-xs mt-1">Add steps below to build the C-103 procedure narrative.</p>
            )}
          </div>
        ) : (
          <ol className="space-y-3">
            {steps.map((step, index) => (
              <li key={index} className="flex gap-3">
                {/* Step number */}
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-sm font-semibold flex items-center justify-center mt-1">
                  {index + 1}
                </div>

                <div className="flex-1 min-w-0">
                  {readOnly ? (
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed print:text-black">
                      {step}
                    </p>
                  ) : (
                    <div>
                      <textarea
                        value={step}
                        onChange={(e) => handleEdit(index, e.target.value)}
                        maxLength={MAX_STEP_CHARS}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                      />
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {step.length}/{MAX_STEP_CHARS}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Controls */}
                {!readOnly && (
                  <div className="flex-shrink-0 flex flex-col gap-1 mt-1">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Move up"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === steps.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Move down"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRemove(index)}
                      className="p-1 text-red-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Remove step"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}

        {/* Add step */}
        {!readOnly && (
          <div className="pt-4 border-t border-gray-200 dark:border-white/[0.05]">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Add Step
            </label>
            <textarea
              value={newStep}
              onChange={(e) => setNewStep(e.target.value)}
              maxLength={MAX_STEP_CHARS}
              rows={3}
              placeholder="Describe the next procedure step..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {newStep.length}/{MAX_STEP_CHARS}
              </span>
              <button
                onClick={handleAdd}
                disabled={!newStep.trim()}
                className="px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Step
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
